import { computed, nextTick } from 'vue';
import dayjs from 'dayjs';
import intersection from 'lodash/intersection';
import uniq from 'lodash/uniq';
import { useRoute, useRouter } from 'vue-router';
import humps from 'humps';
import isEqual from 'lodash/isEqual';
import { useEnabledTypes } from '@/app/community/composables/useEnabledTypes';
import {
  COMMUNITY_ADS_INTERACTION_TYPES,
  COMMUNITY_INTERACTION_CHANNEL_FILTER_TYPES,
  COMMUNITY_INTERACTION_TYPES,
  COMMUNITY_INTERACTIONS_PAGE_SIZE,
  COMMUNITY_MESSAGE_TYPE,
  COMMUNITY_MESSAGE_TYPE_FILTER_WIDGET,
  COMMUNITY_PLATFORM_FILTER_DEFAULT,
  COMMUNITY_ROUTE_NAMES,
  COMMUNITY_ROUTE_PRESET_FILTERS,
  COMMUNITY_SENTIMENT_FILTER_WIDGET,
  DEFAULT_COMMUNITY_INTERACTION_SORT,
  INBOX_SORT_BY,
  INTERNAL_NOTE_SORT_BY,
  TIMESERIES_MAX_BUCKETS,
  TIMESERIES_SCALE_TYPES,
} from '@/app/community/constants';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import { useCommunityStore } from '@/stores/community';
import { useIdentityStore } from '@/stores/identity';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import { differenceBetweenDays } from '@/utils/dateUtils';
import isEmpty from 'lodash/isEmpty';
import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import {
  BOOLEAN_TYPE_VIEW_FILTER_QUERY_MAP,
  communityStartDate,
  MESSAGE_CONTENT_FILTER_FLAG_TYPE_OPTIONS,
  MESSAGE_CONTENT_FILTER_OPTIONS,
  DEFAULT_VIEW_FILTER,
} from '@/app/community/constants/communityFilters';
import isUndefined from 'lodash/isUndefined';
import {
  getBooleansFromSentimentsQueryParam,
  getTagOperatorAndValues,
} from '@/app/community/composables/utils/composable-utils';
import { useCommunityInboxViewStore } from '@/stores/community-inbox-view';
import { useCommunityFilters } from '@/app/community/composables/useCommunityFilters';
import { getUserTimezone } from '@/utils/timezone';

const UTC_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]';

export default function useRouteQuery() {
  // stores and composables
  const { enabledTypes, enabledPlatforms } = useEnabledTypes();
  const { brandCanAccessInternalNotes, canAccessCommunityOverview, canAccessMultiBrandInbox } =
    useCommunityPermissions();
  const $route = useRoute();
  const $router = useRouter();
  const { parseViewFilterQueryToFilters, buildViewFilterQuery } = useCommunityFilters();

  const communityMultiBrandsStore = useCommunityMultiBrandsStore();
  const communityInteractionStore = useCommunityInteractionStore();
  const communityStore = useCommunityStore();
  const identityStore = useIdentityStore();
  const inboxViewStore = useCommunityInboxViewStore();

  const isCustomViewRoute = computed(() => $route.meta?.customViewRoute);

  // raw community query
  const queryParams = computed(() => humps.camelizeKeys($route?.query) || {});
  // base computed
  const isInternalNoteRoute = computed(() => $route.meta?.collaborationRoute);
  const platforms = computed(() => {
    const platformsQuery = queryParams.value.platforms;
    if (isEmpty(platformsQuery)) {
      return enabledPlatforms.value;
    }
    return Array.isArray(platformsQuery) ? platformsQuery : platformsQuery.split(',');
  });

  const messageTypes = computed(() => {
    const messageTypesQuery = queryParams.value.messageTypes;
    return Array.isArray(messageTypesQuery)
      ? messageTypesQuery
      : messageTypesQuery?.split(',') || [];
  });

  const excludedOverviewTypes = computed(() => [
    COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT_MENTION,
  ]);

  // functions
  async function updateQueryParams(queryUpdates, removeExisting) {
    if (isEqual(queryUpdates, $route.query) || $route?.params?.interactionId) return;
    const query = removeExisting ? queryUpdates : { ...$route.query, ...queryUpdates };
    await $router.replace({
      query,
      params: { brandLabel: identityStore.currentBrand.label },
      name: $route.name,
    });
  }

  function getMessagesQueries(queries, routeName) {
    return { ...queries, ...COMMUNITY_ROUTE_PRESET_FILTERS[routeName] };
  }

  function getInteractionTypesBasedOnMessageTypes(internalNote = false) {
    if (!queryParams.value.messageTypes) {
      return [];
    }
    const selectedTypes = [];
    if (internalNote) {
      selectedTypes.push(...messageTypes.value);
      const index = selectedTypes.indexOf(COMMUNITY_MESSAGE_TYPE.ADS_COMMENT);
      if (index > -1) {
        selectedTypes[index] = COMMUNITY_MESSAGE_TYPE.COMMENT;
      }
    } else {
      messageTypes.value.forEach((t) => {
        const items = COMMUNITY_MESSAGE_TYPE_FILTER_WIDGET?.[t]?.includes;
        if (items?.length) {
          selectedTypes.push(...items);
        }
      });
    }

    return uniq(selectedTypes);
  }

  function getArray(queryValue, targetType, defaultValue) {
    const value =
      Array.isArray(queryValue) || queryValue === undefined
        ? queryValue
        : String(queryValue)?.split(',');
    if (targetType) {
      return value?.map((id) => targetType(id)) || defaultValue;
    }
    return value || defaultValue;
  }

  function getBoolean(queryValue, ignoreFalse = true) {
    if (typeof queryValue === 'boolean') {
      return queryValue;
    }
    if (ignoreFalse) {
      return queryValue === 'true' || undefined;
    }
    return queryValue ? queryValue === 'true' : undefined;
  }

  // computed
  const autoGeneratedScale = computed(() => {
    let scale;
    if (canAccessCommunityOverview.value) {
      const dayInterval = Math.abs(
        differenceBetweenDays(queryParams.value.startDate, queryParams.value.endDate),
      );
      if (dayInterval <= TIMESERIES_MAX_BUCKETS) {
        scale = TIMESERIES_SCALE_TYPES.DAY;
      } else if (dayInterval <= TIMESERIES_MAX_BUCKETS * 7) {
        scale = TIMESERIES_SCALE_TYPES.WEEK;
      } else {
        scale = TIMESERIES_SCALE_TYPES.MONTH;
      }
    }
    return scale;
  });

  const selectedMessageTypes = computed(() => getInteractionTypesBasedOnMessageTypes());

  const interactionTypes = computed(() => {
    const platformTypes = platforms.value ?? enabledPlatforms.value;
    const selectedPlatformTypes = platformTypes.flatMap((platform) => {
      const adsTypes = [];
      if (platform === 'INSTAGRAM') {
        adsTypes.push(COMMUNITY_ADS_INTERACTION_TYPES.INSTAGRAM_ADS_COMMENT);
      }
      if (platform === 'FACEBOOK') {
        adsTypes.push(COMMUNITY_ADS_INTERACTION_TYPES.FACEBOOK_ADS_COMMENT);
      }
      return [...COMMUNITY_INTERACTION_CHANNEL_FILTER_TYPES[platform], ...adsTypes];
    });
    if (selectedMessageTypes.value.length) {
      const selected = selectedMessageTypes.value.filter(
        (t) => !COMMUNITY_MESSAGE_TYPE_FILTER_WIDGET[t]?.subtype,
      );
      return intersection(selectedPlatformTypes, selected, enabledTypes.value).join(',');
    }
    return selectedPlatformTypes.filter((type) => enabledTypes.value?.includes(type)).join(',');
  });

  const subtypes = computed(() => {
    const selectedSubtypes = selectedMessageTypes.value?.filter(
      (t) => COMMUNITY_MESSAGE_TYPE_FILTER_WIDGET[t]?.subtype,
    );
    return selectedSubtypes?.length > 0 ? selectedSubtypes : undefined;
  });

  const selectedBrandIds = computed(() => {
    if (!canAccessMultiBrandInbox.value) {
      return undefined;
    }
    const accessibleBrandIds = communityMultiBrandsStore.currentRouteAccessibleBrandIds;
    if (queryParams.value?.brandTagIds?.length) {
      const brandTagIds = getArray(queryParams.value?.brandTagIds, Number);
      return communityMultiBrandsStore.getBrandIdsFromBrandTagIds(brandTagIds);
    }
    if (queryParams.value?.brandIds?.length) {
      const brandIds = getArray(queryParams.value.brandIds, Number);
      return brandIds.filter((brandId) => accessibleBrandIds.includes(brandId));
    }
    if (isCustomViewRoute.value && inboxViewStore.activeView?.id) {
      const brandIds = getArray(inboxViewStore.activeViewFilters.brandIds, Number);
      return brandIds.filter((brandId) => accessibleBrandIds.includes(brandId));
    }
    return accessibleBrandIds;
  });

  const textAnalysisFilters = computed(() => ({
    sentiments: getArray(queryParams.value.sentiments),
    isQuestion: getBoolean(queryParams.value.isQuestion, false),
    responseRecommended: getBoolean(queryParams.value.responseRecommended),
  }));

  const brandIdsQuery = computed(() => {
    if (!selectedBrandIds.value) {
      return [];
    }
    let brandIds = [...selectedBrandIds.value];

    // Filter out brands without text analysis access
    const textAnalysisFiltersApplied = Object.values(textAnalysisFilters.value).some(
      (v) => v !== undefined,
    );
    if (textAnalysisFiltersApplied) {
      brandIds = brandIds.filter((id) =>
        communityMultiBrandsStore.textAnalysisAccessibleBrandIds.includes(id),
      );
    }
    // Filter out brands without inbox automation access
    if (queryParams.value.interactionRuleIds) {
      brandIds = brandIds.filter((id) =>
        communityMultiBrandsStore.interactionRuleAccessibleBrandIds.includes(id),
      );
    }
    return brandIds;
  });

  const selectedMessageContent = computed(() => getArray(queryParams.value.messageContent, String));

  const messageContentQuery = computed(() =>
    selectedMessageContent.value?.filter(
      (c) => !MESSAGE_CONTENT_FILTER_FLAG_TYPE_OPTIONS.includes(c),
    ),
  );

  const isQuestionQuery = computed(
    () =>
      selectedMessageContent.value?.includes(MESSAGE_CONTENT_FILTER_OPTIONS.QUESTION.value) ||
      undefined,
  );

  const isSpamQuery = computed(
    () =>
      selectedMessageContent.value?.includes(MESSAGE_CONTENT_FILTER_OPTIONS.SPAM.value) ||
      undefined,
  );

  // processed community query
  const routeQueries = computed(() => {
    const interactionRuleIds = getArray(queryParams.value.interactionRuleIds, Number);

    return {
      query: queryParams.value.query || undefined,
      brandId: !canAccessMultiBrandInbox.value ? identityStore.currentBrand?.id : undefined,
      brandIds: canAccessMultiBrandInbox.value ? brandIdsQuery.value : undefined,
      sort: queryParams.value.sort ?? DEFAULT_COMMUNITY_INTERACTION_SORT,
      id: queryParams.value.id || null,
      tagIds: queryParams.value.tagIds,
      isArchived: getBoolean(queryParams.value.isArchived, false),
      isFollowUp: getBoolean(queryParams.value.isFollowUp),
      isRead: getBoolean(queryParams.value.isRead, false),
      startDate: queryParams.value.startDate,
      endDate: queryParams.value.endDate,
      sourceUpdatedAfter: queryParams.value.startDate
        ? dayjs
            .tz(queryParams.value.startDate, getUserTimezone())
            .startOf('date')
            .utc()
            .format(UTC_DATETIME_FORMAT)
        : dayjs
            .tz(communityStartDate, getUserTimezone())
            .startOf('day')
            .utc()
            .format(UTC_DATETIME_FORMAT),
      sourceUpdatedBefore: queryParams.value.endDate
        ? dayjs
            .tz(queryParams.value.endDate, getUserTimezone())
            .endOf('date')
            .utc()
            .format(UTC_DATETIME_FORMAT)
        : dayjs().tz(getUserTimezone()).endOf('day').utc().format(UTC_DATETIME_FORMAT),
      mediaIds: getArray(queryParams.value.mediaIds, Number),
      isQuestion: isQuestionQuery.value,
      ticketId: queryParams.value.ticketId || undefined,
      platforms: platforms.value,
      types: interactionTypes.value || undefined,
      scale: queryParams.value.scale || autoGeneratedScale.value,
      assigneeId: Number(queryParams.value.assigneeId) || undefined,
      assigneeIds: getArray(queryParams.value.assigneeIds, Number),
      mentionedUserIds: getArray(queryParams.value.mentionedUserIds, Number),
      resourceId: Number(queryParams.value.resourceId) || undefined,
      messageTypes: messageTypes.value,
      sentiments: getArray(queryParams.value.sentiments),
      interactionRuleIds,
      interactionRuleId: interactionRuleIds?.[0],
      responseRecommended: getBoolean(queryParams.value.responseRecommended),
      messageContent: messageContentQuery.value,
      isSpam: isSpamQuery.value,
      subtype: subtypes.value,
    };
  });

  const messagesCsvDownloadPayload = computed(() => {
    let brandIds;
    let tagIds;
    if (canAccessMultiBrandInbox.value) {
      brandIds = brandIdsQuery.value ?? undefined;
    } else {
      brandIds = [identityStore?.currentBrand?.id].filter((i) => !!i).length
        ? [identityStore.currentBrand.id]
        : undefined;
    }
    if (queryParams.value.tagIds) {
      tagIds = getTagOperatorAndValues(queryParams.value.tagIds, true);
    }

    const {
      query,
      sort,
      isArchived,
      isFollowUp,
      isRead,
      sourceUpdatedAfter,
      sourceUpdatedBefore,
      isQuestion,
      ticketId,
      assigneeId,
      responseRecommended,
      mediaIds,
      types,
      interactionRuleIds,
      messageContent,
      isSpam,
      subtype,
    } = routeQueries.value;

    return omitBy(
      getMessagesQueries(
        {
          query,
          brandIds,
          sort,
          tagIds,
          isArchived,
          isFollowUp,
          isRead,
          sourceUpdatedAfter,
          sourceUpdatedBefore,
          mediaIds,
          isQuestion,
          ticketId,
          types: types?.split(','),
          assigneeId,
          ...getBooleansFromSentimentsQueryParam(queryParams.value.sentiments),
          interactionRuleIds,
          responseRecommended,
          messageContent: messageContent?.join(',') || undefined,
          isSpam,
          subtype: subtype?.join(',') || undefined,
        },
        $route.name,
      ),
      (val) => isUndefined(val) || (Array.isArray(val) && !val.length),
    );
  });

  const sharedQueries = computed(() => {
    const {
      query,
      brandId,
      brandIds,
      isArchived,
      isFollowUp,
      isRead,
      sourceUpdatedAfter,
      sourceUpdatedBefore,
      isQuestion,
      ticketId,
      types,
      sentiments,
      tagIds,
      mediaIds,
      responseRecommended,
      messageContent,
      isSpam,
      subtype,
    } = routeQueries.value;
    return {
      query,
      brandId: brandId || undefined,
      brandIds: brandIds?.length ? brandIds.join(',') : undefined,
      tagIds,
      isArchived,
      isFollowUp,
      isRead,
      sourceUpdatedAfter,
      sourceUpdatedBefore,
      mediaIds: mediaIds ? mediaIds.join(',') : undefined,
      isQuestion,
      isPositive:
        sentiments?.includes(COMMUNITY_SENTIMENT_FILTER_WIDGET.POSITIVE.value) || undefined,
      isNeutral: sentiments?.includes(COMMUNITY_SENTIMENT_FILTER_WIDGET.NEUTRAL.value) || undefined,
      isNegative:
        sentiments?.includes(COMMUNITY_SENTIMENT_FILTER_WIDGET.NEGATIVE.value) || undefined,
      ticketId,
      types,
      responseRecommended,
      messageContent: messageContent?.length ? messageContent.join(',') : undefined,
      isSpam,
      subtype: subtype?.length ? subtype.join(',') : undefined,
    };
  });

  const overviewQueries = computed(() => {
    const { types, brandIds, brandId, ...restSharedQueries } = sharedQueries.value;
    const { interactionRuleIds, assigneeIds } = routeQueries.value;
    return {
      ...restSharedQueries,
      brandIds: canAccessMultiBrandInbox.value ? brandIds : String(brandId),
      types: types
        ? types
            .split(',')
            .filter((type) => !excludedOverviewTypes.value.includes(type))
            .join(',')
        : undefined,
      interactionRuleIds: interactionRuleIds ? interactionRuleIds.join(',') : undefined,
      assigneeIds: assigneeIds ? assigneeIds.join(',') : undefined,
    };
  });

  const formattedOverviewQueriesPayload = computed(() => {
    const formattedQueries = {
      ...overviewQueries.value,
      brandIds: getArray(overviewQueries.value.brandIds, Number),
      interactionRuleIds: getArray(overviewQueries.value.interactionRuleIds, Number),
    };

    // Remove null or undefined fields
    return omitBy(formattedQueries, (q) => isNil(q));
  });

  const messagesQueries = computed(() => {
    const { interactionRuleIds, assigneeIds, assigneeId, sort, id } = routeQueries.value;
    return {
      ...sharedQueries.value,
      interactionRuleIds: interactionRuleIds ? interactionRuleIds.join(',') : undefined,
      assigneeId,
      assigneeIds: assigneeIds?.length ? assigneeIds.join(',') : undefined,
      sort,
      limit: COMMUNITY_INTERACTIONS_PAGE_SIZE,
      id,
    };
  });

  const customViewQueries = computed(() => {
    if (!isCustomViewRoute.value) return {};

    const q = humps.camelizeKeys($route.query) || {};

    // Normalize both sources to FE filters shape
    const viewFiltersFilterPanelFormat = parseViewFilterQueryToFilters(
      inboxViewStore.activeViewFilters,
      false,
    );
    const routeQueriesFilterPanelFormat = parseViewFilterQueryToFilters(routeQueries.value, false);
    // Build a generic checker per field in DEFAULT_VIEW_FILTER
    // treat any boolean drivers of that collection as explicit
    const COLLECTION_DRIVERS = Object.entries(BOOLEAN_TYPE_VIEW_FILTER_QUERY_MAP).reduce(
      (acc, [boolKey, collKey]) => {
        (acc[collKey] ||= new Set()).add(boolKey);
        return acc;
      },
      {},
    );
    const hasOwn = (obj, k) => Object.prototype.hasOwnProperty.call(obj, k);

    const isExplicit = (field) => {
      if (field === 'platforms') return hasOwn(q, 'platforms');
      if (hasOwn(q, field)) return true;
      const boolDrivers = COLLECTION_DRIVERS[field];
      return !!boolDrivers && [...boolDrivers].some((b) => hasOwn(q, b));
    };

    // Merge only the fields defined in DEFAULT_VIEW_FILTER
    const mergedView = {};
    Object.keys(DEFAULT_VIEW_FILTER).forEach((field) => {
      mergedView[field] = isExplicit(field)
        ? routeQueriesFilterPanelFormat[field]
        : (viewFiltersFilterPanelFormat[field] ?? DEFAULT_VIEW_FILTER[field]);
    });

    if (Array.isArray(routeQueries.value?.brandIds)) {
      const allowed = new Set(routeQueries.value.brandIds);
      const current = Array.isArray(mergedView.brandIds) ? mergedView.brandIds : [];

      // If the user explicitly set brandIds, intersect with allowed.
      // If not explicit and current is empty, fall back to allowed to avoid ending up with []
      if (isExplicit('brandIds')) {
        mergedView.brandIds = current.filter((id) => allowed.has(id));
      } else {
        const intersected = current.filter((id) => allowed.has(id));
        mergedView.brandIds = intersected.length ? intersected : routeQueries.value.brandIds;
      }
    }

    // Turn merged view back into BE query format:
    const mergedViewQuery = buildViewFilterQuery(mergedView);

    // messagesQueries already carries non-view params
    const { types: _dropT, subtype: _dropST, ...otherQueries } = messagesQueries.value;
    const finalQuery = {
      ...otherQueries,
      ...mergedViewQuery,
    };

    return omitBy(finalQuery, (qv) => isNil(qv));
  });

  const interactionQueries = computed(() => {
    if ($route.meta?.overviewRoute || $route.meta?.collaborationRoute) {
      return {};
    }
    if (isCustomViewRoute.value) {
      return customViewQueries.value;
    }
    return getMessagesQueries(messagesQueries.value, $route.name);
  });

  const internalNotesQueries = computed(() => {
    const selectedTypes = getInteractionTypesBasedOnMessageTypes(true);
    let isAdComment;
    if (
      messageTypes.value.includes(COMMUNITY_MESSAGE_TYPE.ADS_COMMENT) &&
      !messageTypes.value.includes(COMMUNITY_MESSAGE_TYPE.COMMENT)
    ) {
      isAdComment = true;
    } else if (
      !messageTypes.value.includes(COMMUNITY_MESSAGE_TYPE.ADS_COMMENT) &&
      messageTypes.value.includes(COMMUNITY_MESSAGE_TYPE.COMMENT)
    ) {
      isAdComment = false;
    }
    const meta = {
      ...(platforms.value &&
      platforms.value.length !== 0 &&
      platforms.value.length !== COMMUNITY_PLATFORM_FILTER_DEFAULT.length
        ? { channel: platforms.value.join(',') }
        : {}),
      ...(selectedTypes.length ? { messageType: selectedTypes.join(',') } : {}),
      ...(queryParams.value.isRead !== undefined && !routeQueries.value.isRead
        ? { isRead: routeQueries.value.isRead }
        : {}),
      ...(isAdComment !== undefined ? { isAdComment } : {}),
    };
    const noteSort =
      routeQueries.value.sort === INBOX_SORT_BY.RELEVANCE
        ? DEFAULT_COMMUNITY_INTERACTION_SORT
        : routeQueries.value.sort;

    return {
      brandId: sharedQueries.value.brandId,
      brandIds: sharedQueries.value.brandIds,
      createdAfter: dayjs.utc(routeQueries.value.sourceUpdatedAfter).format(UTC_DATETIME_FORMAT),
      createdBefore: dayjs.utc(routeQueries.value.sourceUpdatedBefore).format(UTC_DATETIME_FORMAT),
      meta: Object.keys(meta).length > 0 ? meta : undefined,
      limit: COMMUNITY_INTERACTIONS_PAGE_SIZE,
      sort: INTERNAL_NOTE_SORT_BY[noteSort],
      mentionedUserIds: queryParams.value.mentionedUserIds,
    };
  });

  // methods with computed dependencies
  async function getUnreadMessageCounts() {
    let mentioned = {};
    if (
      $route?.name !== COMMUNITY_ROUTE_NAMES.INTERNAL_NOTES &&
      !queryParams.value.mentionedUserIds?.length
    ) {
      // current user is selected for mentioned user filter as default
      // use current user id when fetching unread counts on menus aside internal notes
      mentioned = { mentionedUserIds: String(identityStore.identity?.id) };
    }

    const promises = [communityInteractionStore.getUnreadInteractionCountsV2()];
    if (brandCanAccessInternalNotes.value) {
      const query = { ...communityStore.internalNotesQueries, ...mentioned };
      const brandIds = communityStore.internalNotesQueries?.brandIds;
      if (!brandIds?.length) {
        // TODO: Remove brandId check when multi brand is rolled out fully
        const brandId = communityStore.internalNotesQueries.brandId;
        query.brandIds = `${brandId}`;
        delete query.brandId;
      }
      promises.push(communityStore.getUnreadInternalNotesCounts(query));
    }
    await Promise.allSettled(promises);
  }

  async function getActiveViewUnreadMessageCounts() {
    const { sort, limit, ...rest } = customViewQueries.value;
    await inboxViewStore.getActiveViewUnreadCount(rest);
  }

  async function refreshCommunityMessages(
    clearList = false,
    selectFirst = false,
    overrideFilters = {},
    nextPage = true,
  ) {
    if (!interactionQueries.value.brandId && !interactionQueries.value.brandIds) {
      await communityInteractionStore.clearCommunityInteractions();
      return;
    }
    if (clearList) {
      await communityInteractionStore.clearCommunityInteractions();
    }

    let assignment = {};
    if ($route.name === COMMUNITY_ROUTE_NAMES.ASSIGNED_TO_YOU) {
      assignment = { assigneeId: identityStore.identity.id };
    }

    const getInteractionFilters = {
      ...interactionQueries.value,
      ...assignment,
      ...overrideFilters,
    };

    await communityInteractionStore.getCommunityInteractions(
      !clearList && communityInteractionStore.communityInteractionsNextPage && nextPage
        ? {
            next: communityInteractionStore.communityInteractionsNextPage,
          }
        : { ...getInteractionFilters },
    );
    // no need to fetch count again here since there is already a watcher to fetch count for
    // that channel only in CommunityInteractionList.vue
    if (clearList || selectFirst) {
      communityInteractionStore.setSelectedCommunityInteraction(
        communityInteractionStore.filteredCommunityInteractions[0],
      );
    }
  }

  async function refreshInternalNotePreview(
    clearList = false,
    selectFirst = false,
    nextPage = true,
  ) {
    if (!internalNotesQueries.value.brandId && !internalNotesQueries.value.brandIds) {
      await communityInteractionStore.clearInternalNotes();
      return;
    }
    if (clearList) {
      await communityInteractionStore.clearInternalNotes();
    }
    const query = {
      ...internalNotesQueries.value,
      commentId: queryParams.value.resourceId,
    };
    if (internalNotesQueries.value.brandId) {
      query.brandIds = `${internalNotesQueries.value.brandId}`;
    }
    const payload =
      communityInteractionStore.internalNoteNextPage && nextPage
        ? { next: communityInteractionStore.internalNoteNextPage }
        : query;
    await communityInteractionStore.getInternalNotesPreview(payload);
    if (clearList || selectFirst) {
      await communityInteractionStore.setSelectedInternalNote(
        communityInteractionStore.internalNotes[0],
      );
    }
  }

  function handleOverviewAccess(refresh = false) {
    let nextRoute = $route.name;
    const defaultMessagesRoute = COMMUNITY_ROUTE_NAMES.ALL_MESSAGES;
    if ($route.name === 'community' || refresh) {
      if (queryParams.value?.id !== undefined) {
        nextRoute = COMMUNITY_ROUTE_NAMES.ALL_MESSAGES;
      } else {
        nextRoute = canAccessCommunityOverview.value
          ? COMMUNITY_ROUTE_NAMES.INSIGHTS
          : defaultMessagesRoute;
      }
    } else if ($route.meta?.overviewRoute) {
      nextRoute = canAccessCommunityOverview.value ? $route.name : defaultMessagesRoute;
    } else if ($route.meta?.collaborationRoute) {
      nextRoute = brandCanAccessInternalNotes.value ? $route.name : defaultMessagesRoute;
    }
    if (!refresh && nextRoute === $route.name) {
      return;
    }
    nextTick(() => {
      const operation = refresh ? 'push' : 'replace';
      const query = refresh ? {} : queryParams.value;
      $router[operation]({
        name: nextRoute,
        params: { brandLabel: identityStore.currentBrand?.label },
        query,
      });
    });
  }

  async function searchByAuthor(authorName) {
    if (authorName && !isInternalNoteRoute.value) {
      await updateQueryParams({
        query: `From:${authorName}`,
        ticketId: undefined,
        id: undefined,
      });
    }
  }

  return {
    isInternalNoteRoute,
    queryParams,
    getMessagesQueries,
    getUnreadMessageCounts,
    selectedBrandIds,
    routeQueries,
    interactionTypes,
    overviewQueries,
    formattedOverviewQueriesPayload,
    messagesQueries,
    customViewQueries,
    interactionQueries,
    internalNotesQueries,
    platforms,
    autoGeneratedScale,
    excludedOverviewTypes,
    refreshCommunityMessages,
    refreshInternalNotePreview,
    updateQueryParams,
    handleOverviewAccess,
    messagesCsvDownloadPayload,
    searchByAuthor,
    getActiveViewUnreadMessageCounts,
  };
}
